<template>
  <v-card class="secondary" height="100%">
    <v-form ref="form">
      <v-card-title class="font-weight-medium secondary--text primary">
        {{ edit ? `Edit Role` : "Add Role" }}
        <v-spacer></v-spacer>
        <v-btn small icon @click="$emit('closeDialog')">
          <v-icon small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-5">
        <v-row>
          <v-col cols="8">
            <v-text-field
              v-model.trim="form.name"
              label="Role Name*"
              outlined
              dense
              hide-details="auto"
              :readonly="!!edit"
              :rules="[rules.required]"
          /></v-col>
          <v-col cols="4">
            <v-text-field
              v-model="form.code"
              label="Code*"
              outlined
              dense
              maxlength="3"
              hint="3 alphanumeric characters, always saved in capitals"
              persistent-hint
              :readonly="!!edit"
              @input="form.code = form.code ? form.code.toUpperCase() : form.code"
              :rules="[rules.required, codeRule]"
          /></v-col>
          <v-col cols="12">
            <v-switch
              v-model="form.active"
              label="Active"
              hide-details="auto"
              class="mt-0"
            />
          </v-col>
          <v-col cols="12">
            <div class="d-flex align-center mb-2">
              <div class="subtitle-2 secondary--text">
                Menu items ({{ form.menu_item_ids.length }} selected)
              </div>
              <v-spacer></v-spacer>
              <v-btn text x-small color="accent" @click="selectAll">
                Select all
              </v-btn>
              <v-btn text x-small color="accent" @click="clearAll">
                Clear
              </v-btn>
            </div>
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Search menu items"
              outlined
              dense
              hide-details="auto"
              class="mb-2"
            />
            <div
              class="secondary pa-2"
              style="
                max-height: 320px;
                overflow-y: auto;
                border: 1px solid rgba(0, 0, 0, 0.2);
                border-radius: 4px;
              "
            >
              <v-treeview
                v-model="form.menu_item_ids"
                :items="treeItems"
                :search="search"
                item-key="id"
                item-text="title"
                item-children="children"
                selectable
                selection-type="leaf"
                open-on-click
                dense
              >
                <template #prepend="{ item }">
                  <v-icon small color="primary">{{ item.icon || "mdi-menu" }}</v-icon>
                </template>
                <template #label="{ item }">
                  <span>{{ item.title }}</span>
                  <span v-if="item.module_name" class="text-caption grey--text ml-1"
                    >({{ item.module_name }})</span
                  >
                </template>
              </v-treeview>
              <div v-if="!treeItems.length" class="text-caption pa-2">
                No menu items found
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pt-0 mb-3 mx-2">
        <v-spacer></v-spacer>
        <v-btn class="primary" @click="save">{{
          edit ? "Update" : "Save"
        }}</v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>
<script>
import { rules } from "@/helpers/rules";

const HIDDEN_GROUP_ID = "other-pages";

export default {
  props: {
    role: {
      type: Object,
      default: () => {},
    },
    edit: {
      type: Boolean,
      default: () => false,
    },
    menuItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      rules,
      form: {
        name: "",
        code: "",
        active: true,
        menu_item_ids: [],
      },
      search: "",
      codeRule: (v) =>
        /^[A-Za-z0-9]{3}$/.test(v || "") ||
        "Code must be exactly 3 alphanumeric characters",
    };
  },
  computed: {
    treeItems() {
      const byId = {};
      const visible = this.menuItems.filter((item) => item.visible);
      const hidden = this.menuItems.filter((item) => !item.visible);

      visible.forEach((item) => {
        byId[item.id] = { ...item, children: [] };
      });
      const roots = [];
      visible.forEach((item) => {
        const node = byId[item.id];
        if (item.parent_id && byId[item.parent_id]) {
          byId[item.parent_id].children.push(node);
        } else if (!item.parent_id) {
          roots.push(node);
        }
      });
      const bySortOrder = (a, b) => a.sort_order - b.sort_order;
      roots.sort(bySortOrder);
      roots.forEach((root) => root.children.sort(bySortOrder));

      if (hidden.length) {
        roots.push({
          id: HIDDEN_GROUP_ID,
          title: "Other pages (not in sidebar)",
          icon: "mdi-eye-off-outline",
          children: hidden
            .map((item) => ({ ...item, children: [] }))
            .sort((a, b) => a.title.localeCompare(b.title)),
        });
      }

      return roots;
    },
    allLeafIds() {
      return this.menuItems
        .filter((item) => item.module_id)
        .map((item) => item.id);
    },
  },
  created() {
    this.init();
  },
  methods: {
    init() {
      if (this.edit) {
        this.form = {
          id: this.role.id,
          name: this.role.name,
          code: this.role.code,
          active: this.role.active,
          menu_item_ids: (this.role.menu_items || []).map((mi) => mi.id),
        };
      }
    },
    selectAll() {
      this.form.menu_item_ids = [...this.allLeafIds];
    },
    clearAll() {
      this.form.menu_item_ids = [];
    },
    save() {
      if (!this.$refs.form.validate()) return;

      const payload = { ...this.form };

      if (this.edit) {
        this.$emit("update", payload);
      } else {
        this.$emit("create", payload);
      }
    },
  },
};
</script>

<style scoped></style>
